import { motion } from 'framer-motion';

export default function StatsSection() {
  const eligibleBuildings = [
    { 
      name: 'Entrepôts & Logistique', 
      icon: 'ri-building-2-fill', 
      color: 'bg-gradient-to-br from-blue-500 to-blue-600',
      description: 'Zones de stockage et plateformes logistiques'
    },
    { 
      name: 'Usines & Industries', 
      icon: 'ri-building-4-fill', 
      color: 'bg-gradient-to-br from-orange-500 to-red-500',
      description: 'Sites de production et ateliers industriels'
    },
    { 
      name: 'Commerces & GMS', 
      icon: 'ri-store-2-fill', 
      color: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      description: 'Magasins, supermarchés et centres commerciaux'
    },
    { 
      name: 'Bureaux & Tertiaire', 
      icon: 'ri-briefcase-fill', 
      color: 'bg-gradient-to-br from-purple-500 to-indigo-600',
      description: 'Espaces de travail et immeubles de bureaux'
    },
    { 
      name: 'Installations Sportives', 
      icon: 'ri-football-fill', 
      color: 'bg-gradient-to-br from-cyan-500 to-blue-500',
      description: 'Gymnases, stades et complexes sportifs'
    },
    { 
      name: 'Établissements de Santé', 
      icon: 'ri-hospital-fill', 
      color: 'bg-gradient-to-br from-rose-500 to-pink-600',
      description: 'Hôpitaux, cliniques et centres médicaux'
    },
    { 
      name: 'Collectivités', 
      icon: 'ri-government-fill', 
      color: 'bg-gradient-to-br from-amber-500 to-orange-500',
      description: 'Mairies, écoles et bâtiments publics'
    },
    { 
      name: 'Parkings & Sous-sols', 
      icon: 'ri-parking-box-fill', 
      color: 'bg-gradient-to-br from-slate-500 to-gray-600',
      description: 'Parkings couverts et espaces souterrains'
    },
  ];

  const isMobile = window.innerWidth < 768;

  return (
    <section className="relative py-8 sm:py-14 lg:py-20 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden">
      {/* Decorative Elements - Desktop only */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={!isMobile ? {
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.06, 0.03],
            rotate: [0, 90, 0],
          } : {}}
          transition={!isMobile ? { duration: 25, repeat: Infinity, ease: "linear" } : {}}
          className="absolute -top-20 sm:-top-28 lg:-top-32 -left-20 sm:-left-28 lg:-left-32 w-64 sm:w-80 lg:w-96 h-64 sm:h-80 lg:h-96 bg-emerald-500 rounded-full blur-3xl"
          style={{ opacity: 0.03 }}
        ></motion.div>
        <motion.div
          animate={!isMobile ? {
            scale: [1, 1.3, 1],
            opacity: [0.03, 0.06, 0.03],
            rotate: [0, -90, 0],
          } : {}}
          transition={!isMobile ? { duration: 30, repeat: Infinity, ease: "linear" } : {}}
          className="absolute -bottom-20 sm:-bottom-28 lg:-bottom-32 -right-20 sm:-right-28 lg:-right-32 w-64 sm:w-80 lg:w-96 h-64 sm:h-80 lg:h-96 bg-teal-500 rounded-full blur-3xl"
          style={{ opacity: 0.03 }}
        ></motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5 sm:mb-8 lg:mb-12"
        >
          <span className="inline-block text-xs sm:text-sm font-semibold text-emerald-600 tracking-wider uppercase mb-2 sm:mb-3 px-3 sm:px-4 py-1 sm:py-2 bg-emerald-50 rounded-full border border-emerald-100">
            Bâtiments Éligibles
          </span>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2 sm:mb-3">
            À qui s'adresse l'offre ?
          </h2>
          <p className="text-xs sm:text-base text-slate-600 max-w-2xl mx-auto px-2">
            Tous les bâtiments professionnels et collectifs sont éligibles au programme CEE
          </p>
        </motion.div>

        {/* Bâtiments éligibles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {eligibleBuildings.map((building, index) => (
            <motion.div
              key={building.id}
              initial={!isMobile ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
              whileInView={!isMobile ? { opacity: 1, y: 0 } : {}}
              viewport={{ once: true }}
              transition={!isMobile ? { duration: 0.5, delay: index * 0.1 } : {}}
              className={`group relative bg-white rounded-xl border-2 cursor-pointer overflow-hidden ${
                isMobile 
                  ? 'border-slate-200' 
                  : 'transition-all duration-300 hover:border-emerald-400 hover:-translate-y-1 hover:shadow-2xl'
              }`}
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Gradient overlay on hover - Desktop only */}
              {!isMobile && (
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-teal-50/0 group-hover:from-emerald-50/50 group-hover:to-teal-50/30 transition-all duration-300 pointer-events-none"></div>
              )}

              {/* Animated Background - Desktop only */}
              {!isMobile && (
                <motion.div
                  className={`absolute inset-0 ${building.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                ></motion.div>
              )}

              {/* Decorative Corner - Desktop only */}
              {!isMobile && (
                <div className="absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-emerald-500/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              )}

              {/* Icon */}
              <div className="relative z-10 p-3 sm:p-5 flex flex-col items-center text-center">
                <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-xl ${building.color} flex items-center justify-center mb-1.5 sm:mb-3 shadow-md`}>
                  <i className={`${building.icon} text-base sm:text-xl text-white`}></i>
                </div>
                <h4 className="text-[11px] sm:text-sm font-bold text-slate-900 leading-tight mb-0.5 sm:mb-1">
                  {building.name}
                </h4>
                <p className="text-[9px] sm:text-xs text-slate-600 leading-snug px-1">
                  {building.description}
                </p>
              </div>

              {/* Hover indicator - Desktop only */}
              {!isMobile && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={!isMobile ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
          whileInView={!isMobile ? { opacity: 1, y: 0 } : {}}
          viewport={{ once: true }}
          transition={!isMobile ? { duration: 0.6, delay: 0.3 } : {}}
          className="mt-5 sm:mt-8 lg:mt-12 text-center"
        >
          <div className="inline-flex items-center space-x-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 sm:px-5 lg:px-6 py-2 sm:py-3">
            <i className="ri-information-line text-emerald-600 text-xs sm:text-base"></i>
            <p className="text-[10px] sm:text-sm text-slate-700">
              <span className="font-semibold text-emerald-600">Votre bâtiment n'est pas listé ?</span> <a href="mailto:contact@power-reno-pro.fr" className="underline text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer">Contactez-nous</a> pour vérifier votre éligibilité
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
